/**
 * #1060 — the Kalshi account phase for read surfaces, with a failed
 * connection held through the store's own retry attempts.
 *
 * Each retry starts with `status: 'connecting', error: null`, so the raw phase
 * alternates "unavailable" → "connecting" → "unavailable" every attempt (and
 * once a minute after the ladder ends). The store keeps the last failure in
 * `connectFailure` until a connection succeeds, a connect finds no usable
 * keys, a disconnect, or a new identity's key check, so every surface — including one mounted mid-attempt —
 * names the failure steadily instead of inserting and removing it.
 */
import { useAccountStore } from '@/stores/accountStore';
import { kalshiAccountPhase, type KalshiAccountPhase } from '@/lib/kalshiAccountPhase';

export interface KalshiAccountView {
  phase: KalshiAccountPhase;
  /** The standing connection failure (the store's latch) while not connected,
   *  whatever the phase shows first — a failed key check takes precedence in
   *  the wording, but the connection has still failed. */
  failure: string | null;
}

export function useKalshiAccountPhase(): KalshiAccountView {
  const status = useAccountStore((s) => s.status);
  const credentialsStatus = useAccountStore((s) => s.credentialsStatus);
  const credentialCount = useAccountStore((s) => s.credentialCount);
  const connectFailure = useAccountStore((s) => s.connectFailure);
  const connectWanted = useAccountStore((s) => s.connectWanted);
  const error = useAccountStore((s) => s.error);
  const phase = kalshiAccountPhase({ status, credentialsStatus, credentialCount, connectWanted });
  const failure = status === 'connected' ? null
    : phase === 'unavailable' ? connectFailure ?? error ?? 'Kalshi connection failed.' : connectFailure;
  if (phase === 'connecting' && failure) return { phase: 'unavailable', failure };
  return { phase, failure };
}
